import * as React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box, List, ListItem,
    Stack,
    Typography
} from "@mui/material";
import {useEffect, useState} from "react";
import {ReviewForecast, ReviewForecastPoint} from "../types/stats";
import DashboardPanel from "./DashboardPanel";

export interface ReviewForecastProps {
    forecast: ReviewForecast
    initialReviews: number
    ready: boolean
}

interface ForecastPointData extends Omit<ReviewForecastPoint, "time"> {}

const ReviewForecastListView = ({forecast, initialReviews, ready} : ReviewForecastProps) => {

    const [selectedDay, setSelectedDay] = useState<number>(0);

    const [distribution, setDistribution] = useState<Map<number, Map<number, ForecastPointData>> | null>(null);
    const [dayDistributions, setDayDistributions] = useState<Map<number, ForecastPointData> | null>(null);



    useEffect(() => {
        init();
    }, [ready]);

    const monthDays = [31, new Date().getFullYear() % 4 ? 28 : 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    function init() {
        if (!ready)
            return;

        const newDistribution = new Map<number, Map<number, ForecastPointData>>();
        const newDayDistributions = new Map<number, ForecastPointData>();

        for (let i = 0 ; i <= 6; i++) {
            newDistribution.set(i, new Map());
            newDayDistributions.set(i, {newReviews: 0, total: 0});
        }

        const currentDate = new Date();
        forecast.forEach(point => {
            const d = new Date(point.time);
            const dayOffset = d.getDate() - currentDate.getDate() + (d.getDate() >= currentDate.getDate() ?  0 : monthDays[currentDate.getMonth()]);
            if (dayOffset <= 6) {
                newDistribution.get(dayOffset)!.set(d.getHours(), point);
                newDayDistributions.get(dayOffset)!.newReviews += point.newReviews;
                newDayDistributions.get(dayOffset)!.total = Math.max(newDayDistributions.get(dayOffset)!.total, point.total);
            }
        });
        for (let i = 0; i <= 6; i++) {
            if (newDayDistributions.get(i)!.total === 0) {
                if (i > 0)
                    newDayDistributions.get(i)!.total = newDayDistributions.get(i - 1)!.total;
                else
                    newDayDistributions.get(i)!.total = initialReviews;
            }
        }
        let day = 0;
        for (; day <= 6 && newDistribution.get(day)!.size === 0; day++) {}
        setSelectedDay(day);
        setDistribution(newDistribution);
        setDayDistributions(newDayDistributions);
    }

    const dayOfWeekByOffset = (dayOffset : number) : string => {
        if (dayOffset === 0)
            return "Today";
        const target = (new Date().getDay() + dayOffset) % 7;
        return {
            0: "Sunday",
            1: "Monday",
            2: "Tuesday",
            3: "Wednesday",
            4: "Thursday",
            5: "Friday",
            6: "Saturday",
        } [target]!;
    };

    function reviewCountTypographyView({newReviews, total} : ForecastPointData) {
        return (
            <Typography variant="body1">
                <span style={{display: 'inline-block', minWidth: '4ch', textAlign: 'right'}}>
                    {`+${newReviews}`}
                </span>
                <span> &#8594; </span>
                <span style={{display: 'inline-block', minWidth: '3ch', textAlign: 'right'}}>
                    {`${total}`}
                </span>
            </Typography>
        )
    }

    function dayDistributionListView(day : number) {
        if (distribution!.get(day)!.size === 0) {
            return <Typography sx={{ml: 1}}>No reviews</Typography>
        }

        const maxReviews = Array.from(distribution!.get(day)!.keys()).reduce((max, hour) => Math.max(max, distribution!.get(day)!.get(hour)!.newReviews), 0);

        return (
            <List sx={{py: 0, backgroundColor: 'primary.light', color: 'primary.contrastText', borderRadius: 2}}>
                {Array.from(distribution!.get(day)!.keys()).map((hour, idx) => (
                    <ListItem key={idx} sx={{borderRadius: 2, px: 1}}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" height={15}>
                            <Typography variant="body1">{`${String(hour).padStart(2, '0')}:00`}</Typography>
                            <Box flexGrow={1} px={1}>
                                <Box bgcolor={theme => theme.palette.info.main}
                                     height={15}
                                     width={`${distribution!.get(day)!.get(hour)!.newReviews * 100 / maxReviews}%`}
                                     borderRadius={2}
                                />
                            </Box>
                            <Box width={90}>
                                {reviewCountTypographyView(distribution!.get(day)!.get(hour)!)}
                            </Box>
                        </Box>

                    </ListItem>
                ))}
            </List>
        )
    }

    function singleDayAccordionView(day : number) {
        return (
            <Accordion key={day}
                       square={true}
                       expanded={day === selectedDay}
                       onChange={() => setSelectedDay(selectedDay === day ? -1 : day)}
                       elevation={0}
                       disableGutters={true}
                       sx={{border: '1px solid gray',
                           borderRadius: 3,
                           bgcolor: 'primary.main',
                           mb: 1,
                           '&:before': {
                               display: 'none',
                           }}}
            >
                <AccordionSummary sx={{my: 0, py: 0, height: 10}}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography variant={day === selectedDay ? "h6" : "body1"}>{dayOfWeekByOffset(day)}</Typography>
                        {reviewCountTypographyView(dayDistributions!.get(day)!)}
                    </Box>

                </AccordionSummary>
                <AccordionDetails sx={{px: 1, pt: 0}}>
                    {dayDistributionListView(day)}
                </AccordionDetails>
            </Accordion>
        )
    }

    return (
        <DashboardPanel title="Upcoming reviews" ready={ready} skeletonHeight="100%" height="100%">
            <Box sx={{px: 2, py: 2}}>
                <Stack direction="column" py={2}>
                    {distribution && Array.from(distribution.keys()).map(day => singleDayAccordionView(day))}
                </Stack>
            </Box>
        </DashboardPanel>
    );
}

export default ReviewForecastListView;